import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../lib/api';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { 
  Users, 
  Stethoscope, 
  Building2, 
  CalendarDays,
  PhoneCall,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';

const StatCard = ({ icon: Icon, value, label, color, testId }) => (
  <div className="stat-card hover-lift" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="stat-value">{value}</p>
        <p className="stat-label">{label}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

const statusColors = {
  confirmed: 'badge-confirmed',
  pending: 'badge-pending',
  cancelled: 'badge-cancelled',
  completed: 'badge-completed'
};

const statusLabels = {
  confirmed: 'Confermato',
  pending: 'In attesa',
  cancelled: 'Annullato',
  completed: 'Completato'
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Dashboard stats error:', err);
      // Show default empty stats instead of error toast on load
      setStats({
        total_patients: 0,
        total_doctors: 0,
        total_departments: 0,
        today_appointments: 0,
        pending_appointments: 0,
        recent_calls: 0,
        appointment_stats: {},
        recent_appointments: []
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageHeader title="Dashboard" subtitle="Panoramica del sistema" />
        <div className="content-area">
          <div className="dashboard-grid">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="stat-card">
                <div className="skeleton h-8 w-16 mb-2" />
                <div className="skeleton h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader 
        title="Dashboard" 
        subtitle={`Bentornato! Oggi hai ${stats?.today_appointments || 0} appuntamenti.`}
      />
      
      <div className="content-area" data-testid="dashboard-content">
        {/* Stats Grid */}
        <div className="dashboard-grid mb-8">
          <StatCard
            icon={Users}
            value={stats?.total_patients || 0}
            label="Pazienti Totali"
            color="bg-sky-500"
            testId="stat-patients"
          />
          <StatCard
            icon={Stethoscope}
            value={stats?.total_doctors || 0}
            label="Medici Attivi"
            color="bg-emerald-500"
            testId="stat-doctors"
          />
          <StatCard
            icon={Building2}
            value={stats?.total_departments || 0}
            label="Reparti"
            color="bg-amber-500"
            testId="stat-departments"
          />
          <StatCard
            icon={CalendarDays}
            value={stats?.today_appointments || 0}
            label="Appuntamenti Oggi"
            color="bg-violet-500"
            testId="stat-today-appointments"
          />
          <StatCard
            icon={Clock}
            value={stats?.pending_appointments || 0}
            label="In Attesa"
            color="bg-orange-500"
            testId="stat-pending"
          />
          <StatCard
            icon={PhoneCall}
            value={stats?.recent_calls || 0}
            label="Chiamate (7gg)"
            color="bg-rose-500"
            testId="stat-calls"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Appointments */}
          <div className="table-container">
            <div className="table-header px-5 py-4 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Ultimi Appuntamenti
              </h3>
              <Link 
                to="/appointments" 
                className="text-sm text-sky-600 hover:text-sky-700 flex items-center gap-1"
              >
                Vedi tutti <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {stats?.recent_appointments?.length > 0 ? (
                stats.recent_appointments.map((appt) => (
                  <div 
                    key={appt.id} 
                    className="px-5 py-3 flex items-center justify-between hover:bg-slate-50"
                    data-testid={`recent-appt-${appt.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-800">{appt.patient_name}</p>
                      <p className="text-xs text-slate-500">
                        {appt.date} alle {appt.time} - {appt.doctor_name}
                      </p>
                    </div>
                    <span className={`badge ${statusColors[appt.status] || 'bg-slate-100 text-slate-600'}`}>
                      {statusLabels[appt.status] || appt.status}
                    </span>
                  </div>
                ))
              ) : (
                <div className="px-5 py-8 text-center text-slate-500 text-sm">
                  Nessun appuntamento recente
                </div>
              )}
            </div>
          </div>

          {/* Appointment Stats */}
          <div className="table-container">
            <div className="table-header px-5 py-4">
              <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Stato Appuntamenti
              </h3>
            </div>
            <div className="p-5 space-y-4">
              {Object.entries(stats?.appointment_stats || {}).map(([status, count]) => (
                <div key={status} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">
                        {statusLabels[status] || status}
                      </span>
                      <span className="text-sm font-medium text-slate-800">{count}</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          status === 'confirmed' ? 'bg-emerald-500' :
                          status === 'pending' ? 'bg-amber-500' :
                          status === 'cancelled' ? 'bg-red-500' :
                          status === 'completed' ? 'bg-sky-500' : 'bg-slate-400'
                        }`}
                        style={{ 
                          width: `${Math.min(100, (count / Math.max(...Object.values(stats?.appointment_stats || {1: 1}))) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {Object.keys(stats?.appointment_stats || {}).length === 0 && (
                <div className="text-center text-slate-500 text-sm py-4">
                  Nessuna statistica disponibile
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link 
            to="/appointments" 
            className="stat-card hover-lift flex items-center gap-4 cursor-pointer"
            data-testid="quick-action-appointments"
          >
            <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
              <CalendarDays className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800">Nuovo Appuntamento</p>
              <p className="text-sm text-slate-500">Prenota una visita</p>
            </div>
          </Link>
          
          <Link 
            to="/patients" 
            className="stat-card hover-lift flex items-center gap-4 cursor-pointer"
            data-testid="quick-action-patients"
          >
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800">Gestione Pazienti</p>
              <p className="text-sm text-slate-500">Anagrafica e import</p>
            </div>
          </Link>
          
          <Link 
            to="/call-logs" 
            className="stat-card hover-lift flex items-center gap-4 cursor-pointer"
            data-testid="quick-action-calls"
          >
            <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
              <PhoneCall className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800">Log Chiamate</p>
              <p className="text-sm text-slate-500">Cronologia VoIP</p>
            </div>
          </Link>
        </div>
      </div>
    </Layout>
  );
}
